import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, inject, Injectable, Injector, signal } from '@angular/core';
import config from '@config';
import * as Sentry from '@sentry/angular';
import { AuthRequiredError } from '@services/auth-required-error';
import { AuthService } from '@services/auth.service';
import { AuthStorage } from '@services/auth.storage';
import { HttpError } from '@services/http-error';
import { LogOptions } from '@shared/models/log-options.model';
import { LogUser } from '@shared/models/log-user.model';
import { isError, isObject, isString } from 'lodash';

// Auth-state keys attached to Sentry events. Never add token keys (access/refresh/Cognito) here.
const DIAGNOSTIC_STORAGE_KEYS = [
  AuthStorage.USER,
  AuthStorage.USER_OLD,
  AuthStorage.ROLE,
  AuthStorage.REFRESH_TOKEN_EXPIRATION,
  AuthStorage.TOKENIZED_LOGIN,
  AuthStorage.LOGOUT_INITIATED,
];

@Injectable({ providedIn: 'root' })
export class LogService implements ErrorHandler {
  private injector = inject(Injector);
  user = signal<LogUser | null>(null);

  constructor() {
    this.init();
    setTimeout(() => this.watchUser());
  }

  private init(): void {
    const integrations = [];
    if (config.sentry.replayEnabled && !this.isBotTraffic()) {
      integrations.push(Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false }));
    }
    if (config.sentry.tracingEnabled) {
      integrations.push(Sentry.browserTracingIntegration());
    }
    Sentry.init({
      dsn: config.sentry.dsn,
      environment: config.name,
      release: config.version,
      enabled: config.sentry.enabled,
      integrations,
      tracesSampler: ({ name, inheritOrSampleWith }) => {
        return name.includes('error') ? 1.0 : inheritOrSampleWith(0.1);
      },
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 1.0,
      denyUrls: [
        // Google Adsense
        /pagead\/js/i,
        // Facebook flakiness
        /graph\.facebook\.com/i,
        // Facebook blocked
        /connect\.facebook\.net\/en_US\/all\.js/i,
        // Chrome extensions
        /extensions\//i,
        /^chrome:\/\//i,
        // Other plugins
        /webappstoolbarba\.texthelp\.com\//i,
        /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
      ],
    });
  }

  private isBotTraffic(): boolean {
    // Synthetic/monitoring traffic (e.g. the token-pharmacy terminal health-check bot) hits
    // production continuously via HeadlessChrome, flooding session replay with low-value recordings.
    return /HeadlessChrome/i.test(navigator.userAgent);
  }

  private watchUser(): void {
    const authService = this.injector.get(AuthService);
    authService.authStatusChanged.subscribe(isAuthenticated => {
      if (isAuthenticated) {
        const currentUser = authService.getCurrentUser();
        this.user.set({
          email: currentUser.email,
          username: currentUser.username,
          role: currentUser.role,
        });
      } else {
        this.user.set(null);
      }
      Sentry.setUser(this.user());
    });
  }

  message(message: string, options?: LogOptions): void {
    this.log(message, options);
  }

  messageWithAction(message: string, action: string, options?: LogOptions): void {
    options = Object.assign(options || {}, { tags: { action } });
    this.message(message, options);
  }

  error(error: Error, options?: LogOptions): void {
    if (error instanceof HttpErrorResponse) {
      error = new HttpError(error);
    }
    if (!(error instanceof Error)) {
      error = new Error(this.getErrorMessage(error));
    }
    this.log(error, options);
  }

  errorWithAction(message: string, action: string, options?: LogOptions): void {
    options = Object.assign(options || {}, { tags: { action } });
    this.error(new Error(message), options);
  }

  handleError(error: Error): void {
    this.error(error);
  }

  private log(item: Error | string, options?: LogOptions): void {
    this.logToConsole(item, options);
    const defaultSeverity = isError(item) ? 'error' : 'info';

    if (!config.sentry.enabled) {
      return;
    }

    if (item instanceof HttpError) {
      // Already classified and reported (or intentionally suppressed) by LoggingInterceptor.
      return;
    }

    if (item instanceof AuthRequiredError || (isError(item) && item.cause instanceof AuthRequiredError)) {
      // Auth-required request aborted because the session is gone (logout in progress) — not actionable.
      // `cause` covers wrappers such as Angular's ResourceValueError from an errored rxResource.
      return;
    }

    if (item instanceof Error && item.message.includes('ExpressionChangedAfterItHasBeenCheckedError')) {
      return;
    }

    Sentry.withScope(scope => {
      scope.setLevel(options?.severity ?? defaultSeverity);
      scope.setUser(this.user());
      scope.setExtras({ ...options?.extraInfo, ...this.getLocalStorage() });
      scope.setTags(options?.tags ?? {});
      if (isError(item)) {
        scope.captureException(item);
      } else {
        scope.captureMessage(item);
      }
    });
  }

  private logToConsole(logItem: Error | string, options?: LogOptions): void {
    if (isError(logItem)) {
      console.error(logItem);
    } else {
      console.log(logItem);
    }
    if (options) {
      console.debug(options);
    }
  }

  private getErrorMessage(error: unknown): string {
    if (isObject(error) && 'message' in error && isString(error.message)) {
      return error.message;
    }
    return JSON.stringify(error);
  }

  private getLocalStorage() {
    const result: Record<string, string> = {};
    DIAGNOSTIC_STORAGE_KEYS.forEach(key => (result[key] = localStorage.getItem(key) || ''));
    return result;
  }
}
